import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, File, X, Loader2, CheckCircle2, AlertCircle, MapPin } from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { bytes, asText } from "@/lib/format";
import { useSession } from "@/contexts/SessionContext";

const ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf";

export default function UploadBatch() {
  const navigate = useNavigate();
  const { currentOutletId, outlets, contextValidation, has } = useSession();
  const outletId = currentOutletId;
  const outletName = (outlets || []).find((o) => o.id === outletId)?.name || outletId;
  const currentOutlet = outletId ? { name: outletName, id: outletId } : null;
  const canUpload = has("BULK_CAPTURE_UPLOAD");
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [working, setWorking] = useState(false);
  const inputRef = useRef(null);

  const addFiles = useCallback((incoming) => {
    const list = Array.from(incoming || []);
    setFiles((prev) => {
      const have = new Set(prev.map((p) => `${p.file.name}-${p.file.size}`));
      const next = [...prev];
      for (const f of list) {
        const key = `${f.name}-${f.size}`;
        if (have.has(key)) continue;
        const ok = /\.(jpg|jpeg|png|webp|pdf)$/i.test(f.name);
        next.push({ file: f, status: ok ? "ready" : "rejected", error: ok ? null : "Unsupported format" });
      }
      return next;
    });
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const remove = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));
  const clear = () => setFiles([]);

  const valid = useMemo(() => files.filter((f) => f.status === "ready"), [files]);

  const startBatch = async () => {
    if (!outletId) {
      toast.error("Select an outlet before uploading");
      return;
    }
    if (valid.length === 0) {
      toast.error("Add at least one supported file");
      return;
    }
    setWorking(true);
    try {
      // 1. Pre-flight EC context validation
      const v = await api.validateContext();
      if (!v?.valid) {
        toast.error(
          v?.message || `Context validation failed (${v?.error_code || "unknown"})`
        );
        setWorking(false);
        return;
      }
      const batch = await api.createBatch(notes);
      const rawFiles = valid.map((f) => f.file);
      const res = await api.uploadFiles(batch.batch_id, rawFiles, true);
      const stored = res.files.filter((f) => f.upload_status === "STORED").length;
      const rejected = res.files.filter((f) => f.upload_status === "REJECTED").length;
      toast.success(`Batch created — ${stored} file(s) queued${rejected ? `, ${rejected} rejected` : ""}`);
      navigate(`/batches/${batch.batch_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || e.message || "Upload failed";
      toast.error(msg);
    } finally {
      setWorking(false);
    }
  };

  if (!canUpload) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-[28px] sm:text-[32px] font-semibold text-archival-primary leading-tight tracking-tight">
            Upload Bills
          </h1>
        </header>
        <div
          data-testid="upload-capability-blocked"
          className="rounded-md border border-red-200 bg-red-50 p-8 text-center"
        >
          <div className="text-[16px] font-semibold text-red-900 mb-1">Upload disabled</div>
          <p className="text-[13px] text-red-800">
            Your account is missing the <span className="font-mono">BULK_CAPTURE_UPLOAD</span> capability.
            Contact your administrator if you need access.
          </p>
        </div>
      </div>
    );
  }

  if (!outletId) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-[28px] sm:text-[32px] font-semibold text-archival-primary leading-tight tracking-tight">
            Upload Bills
          </h1>
          <p className="text-[14px] text-archival-secondary mt-1 max-w-2xl">
            Each file becomes a Capture Candidate for review.
          </p>
        </header>
        <div
          data-testid="upload-blocked-no-outlet"
          className="rounded-md border border-amber-200 bg-amber-50 p-8 text-center"
        >
          <MapPin size={28} className="mx-auto text-amber-700 mb-2" />
          <div className="text-[16px] font-semibold text-amber-900 mb-1">
            Select outlet before uploading bills
          </div>
          <p className="text-[13px] text-amber-800/90 max-w-md mx-auto">
            Every batch must be tied to a specific outlet. Pick a BENO outlet from
            the switcher in the header to enable upload.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-[28px] sm:text-[32px] font-semibold text-archival-primary leading-tight tracking-tight">
            Upload Bills
          </h1>
          <p className="text-[14px] text-archival-secondary mt-1 max-w-2xl">
            Upload multiple bill images or PDFs into{" "}
            <span className="font-semibold text-archival-primary">
              {currentOutlet?.name || outletId}
            </span>
            . Each file becomes a{" "}
            <span className="font-semibold text-archival-primary">Capture Candidate</span> for review.
            <span className="block text-[12px] text-archival-muted mt-1">
              Files are not bills. Bills are only created when you click <em>Confirm</em> on a candidate.
            </span>
          </p>
        </div>
        {contextValidation?.valid === false && (
          <div data-testid="context-invalid-notice" className="text-[11px] text-red-700 max-w-xs text-right">
            {asText(contextValidation.message) || "Select an outlet you have access to before uploading."}
          </div>
        )}
      </header>

      <div
        data-testid={TID.uploadDropzone}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={cn(
          "rounded-md border-2 border-dashed bg-archival-surface p-10 flex flex-col items-center justify-center text-center cursor-pointer transition-colors",
          dragOver ? "border-action bg-archival-soft" : "border-archival hover:bg-archival-soft"
        )}
      >
        <Upload size={28} className="text-archival-secondary mb-3" strokeWidth={1.5} />
        <p className="text-[15px] font-semibold text-archival-primary">Drop bill files here</p>
        <p className="text-[13px] text-archival-secondary mt-1">JPG, PNG, WEBP, PDF supported</p>
        <Button
          type="button"
          variant="outline"
          className="mt-4 border-archival bg-white hover:bg-archival-soft"
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
        >
          Choose Files
        </Button>
        <input
          ref={inputRef}
          data-testid={TID.uploadFileInput}
          type="file"
          accept={ACCEPT}
          multiple
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {files.length > 0 && (
        <div className="rounded-md border border-archival bg-archival-surface overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-archival">
            <div className="text-[14px] font-semibold text-archival-primary">
              {files.length} file{files.length === 1 ? "" : "s"} selected
              <span className="ml-2 text-[12px] text-archival-muted font-normal">
                {valid.length} ready · {files.length - valid.length} rejected
              </span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              data-testid={TID.uploadClearAll}
              onClick={clear}
              className="text-archival-secondary hover:text-archival-primary"
            >
              Clear All
            </Button>
          </div>
          <ul>
            {files.map((f, i) => (
              <li
                key={`${f.file.name}-${i}`}
                data-testid={TID.uploadFileRow(i)}
                className="flex items-center gap-3 px-4 py-2.5 border-b border-archival last:border-b-0"
              >
                <File size={16} className="text-archival-secondary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-archival-primary truncate">{f.file.name}</div>
                  <div className="text-[11px] text-archival-muted font-mono">
                    {bytes(f.file.size)} · {f.file.type || "unknown"}
                  </div>
                </div>
                {f.status === "rejected" ? (
                  <span className="inline-flex items-center gap-1 text-[11px] text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full font-medium">
                    <AlertCircle size={11} /> {f.error}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full font-medium">
                    <CheckCircle2 size={11} /> Ready
                  </span>
                )}
                <button
                  onClick={() => remove(i)}
                  data-testid={TID.uploadFileRemove(i)}
                  className="text-archival-muted hover:text-archival-primary transition-colors"
                  aria-label="Remove"
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-md border border-archival bg-archival-surface p-4 space-y-3">
        <div>
          <Label htmlFor="notes" className="text-[12px] font-semibold uppercase tracking-wider text-archival-secondary">
            Batch notes (optional)
          </Label>
          <Textarea
            id="notes"
            data-testid={TID.uploadNotes}
            placeholder="e.g. March 2026 historical bills — Hub Outlet"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mt-1.5 bg-white border-archival resize-none"
            rows={2}
          />
        </div>
        <div className="flex justify-end">
          <Button
            data-testid={TID.uploadStart}
            onClick={startBatch}
            disabled={valid.length === 0 || working}
            className="bg-action text-white hover:opacity-95 disabled:opacity-50"
            style={{ backgroundColor: "var(--action)" }}
          >
            {working ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
            Create Batch & Start Processing
          </Button>
        </div>
      </div>
    </div>
  );
}
